import { formatNumber } from '@/helpers/format-number'
import { GET_DETAIL_EMPLOYEE, UPDATE_EMPLOYEE } from '@/services/api/master/employees'
import { cloneDeep } from '@apollo/client/utilities'
import { parsePhoneNumber } from 'libphonenumber-js'
import { GET_USER_GROUP } from '@/services/api/setting/user/userGroup'
import store from '@/state/store'
import { Helper } from '@/mixin/helper'

export default {
  computed: {
    hasTmsUserGroupAccess() {
      const hasAccessAll = store.getters['identity/hasAccessAll']
      return hasAccessAll(['tms-user-group:read'])
    },
    infoFields() {
      const fields = [
        {
          name: 'user_group_id',
          isShow: this.hasTmsUserGroupAccess && Helper.methods.isSuperUser(),
          label: this.$t('global.field.userGroup'),
          type: 'value-help-v2',
          placeholder: `${this.$t('global.form.select')} ${this.$t('global.field.userGroup')}`,
          trackBy: 'id',
          displayedBy: 'name',
          class: 'col-12',
          listGetter: async (opts) => {
            let result = {}
            const filter = {
              pagination: {
                page: opts?.page,
                show: opts?.limit,
              },
              search: opts?.search,
            }

            const res = await this.$apollo.query({
              query: GET_USER_GROUP,
              fetchPolicy: 'no-cache',
              variables: {
                filter: {
                  ...filter,
                },
              },
            })

            const ugroups = res.data.ugroups.ugroups

            result = {
              metadata: {
                total_count: ugroups.pagination.total,
                page: ugroups.pagination.page,
              },
              data: {
                paginated_result: ugroups.data,
              },
            }

            return result
          },
          tableFields: [
            {
              label: this.$t('global.field.userGroup'),
              key: 'name',
              isSortable: false,
            },
            {
              label: this.$t('global.field.vehicleGroup'),
              key: 'vgroups',
              isSortable: false,
              formatter: (value) => {
                return value?.length != null ? (value.length === 0 ? this.$t('global.text.allVehicleGroup') : value.length) : '-'
              },
            },
          ],
          isMandatory: true,
        },
        {
          name: 'vehicle_id',
          isShow: true,
          label: this.$t('fleetTaskInstant.field.licensePlate'),
          type: 'value-help-v2',
          placeholder: this.$t('fleetTaskInstant.placeholder.licensePlate'),
          trackBy: 'id',
          displayedBy: 'license_plate',
          class: 'col-6',
          tableFields: [
            {
              key: 'license_plate',
              label: this.$t('fleetTaskInstant.field.licensePlate'),
            },
            {
              key: 'vehicle_type.type',
              label: this.$t('fleetTaskInstant.field.vehicleType'),
            },
            {
              key: 'fleet_task.id',
              label: 'Status',
              help: true,
              tooltip: this.$t('fleetTaskInstant.tooltip.vehicleStatus'),
              isStatus: true,
              formatter: (val) => {
                if (val == null) {
                  return {
                    text: this.$t('fleetTaskInstant.option.available'),
                    class: 'mc-status mc-status--green',
                  }
                } else {
                  return {
                    text: this.$t('fleetTaskInstant.option.onAssignment'),
                    class: 'mc-status mc-status--yellow',
                  }
                }
              },
            },
            {
              key: 'distance',
              label: this.$t('global.field.distance'),
              help: true,
              tooltip: this.$t('fleetTaskInstant.tooltip.distance'),
              formatter: (distance) => {
                if (!distance) return '-'

                if (distance >= 1000) {
                  return `${formatNumber(distance / 1000)} km`
                }
                return '< 1 km'
              },
              thStyle: 'min-width: 80px',
            },
            {
              key: 'fuel',
              label: this.$t('global.field.fuel'),
              formatter: (fuel) => {
                if (!fuel) return '-'
              },
            },
            {
              key: 'driver.name',
              label: this.$t('global.field.driver'),
            },
            {
              key: 'fleet_task.estimated_arrival_on',
              label: this.$t('global.field.estimatedCompletion'),
              type: 'datetime',
            },
            {
              key: 'fleet_task',
              label: this.$t('global.field.fleetTaskNumber'),
              type: 'redirectable',
              redirectTo: {
                FLEET_TASK: 'Fleet Task Detail',
                FLEET_TASK_INSTANT: 'Fleet Task Instant Detail',
              },
              redirectId: 'id',
              redirectLabel: 'number',
              formatter: (item) => {
                if (!item) {
                  return ''
                }

                if (this.hasTmsUserGroupAccess && !Helper.methods.isSuperUser()) {
                  const currentUserGroupId = store.state.fleetTaskInstant?.user_group?.id

                  if (
                    (item.user_group?.id &&
                      currentUserGroupId &&
                      item.user_group.id !== currentUserGroupId) ||
                    item.user_group === null
                  ) {
                    return '-'
                  }
                }

                return item
              },
              isClickable: (item) => {
                if (this.hasTmsUserGroupAccess && !Helper.methods.isSuperUser()) {
                  const currentUserGroupId = store.state.fleetTaskInstant?.user_group?.id

                  return (
                    item?.user_group !== null &&
                    item?.user_group?.id &&
                    currentUserGroupId &&
                    item.user_group.id === currentUserGroupId
                  )
                }

                return item !== null
              },
            },
          ],
          filterOptions: {
            default: null,
            options: [
              {
                key: 'all',
                label: this.$t('fleetTaskInstant.option.all'),
                value: null,
              },
              {
                key: 'available',
                label: this.$t('fleetTaskInstant.option.available'),
                value: true,
              },
              {
                key: 'not_available',
                label: this.$t('fleetTaskInstant.option.onAssignment'),
                value: false,
              },
            ],
          },
        },
        {
          name: 'driver_id',
          isShow: true,
          label: this.$t('global.field.driver'),
          type: 'value-help-v2',
          placeholder: this.$t('fleetTaskInstant.placeholder.driver'),
          trackBy: 'id',
          displayedBy: 'name',
          class: 'col-6',
          hasUpdateAction: true,
          disableEditWhen: (row) => row.item.fleet_task !== null,
          apiClient: {
            update: async (id, input, _) => {
              const newData = cloneDeep(input)
              newData['phoneNo'] = parsePhoneNumber(input['phoneNo'], 'ID').format('E.164')

              const res = await this.$apollo.mutate({
                mutation: UPDATE_EMPLOYEE,
                variables: {
                  input: {
                    userId: parseInt(id),
                    oldData: {},
                    newData,
                  },
                },
              })

              if (!res.data.updateEmployee.isSuccessful) {
                return {
                  errors: true,
                  message: res.data.updateEmployee.message,
                }
              }

              return res
            },
            show: async (id) => {
              const response = await this.$apollo.query({
                query: GET_DETAIL_EMPLOYEE,
                fetchPolicy: 'no-cache',
                variables: {
                  filter: {
                    userId: parseInt(id),
                  },
                },
              })
              return {
                name: response.data.employee.employee.fullname,
                phone_no: response.data.employee.employee.phoneNo,
              }
            },
          },
          tableFields: [
            {
              inputName: 'fullname',
              key: 'name',
              label: this.$t('global.field.driver'),
              isSortable: true,
              isEditable: true,
              validation: 'required:trim',
              placeholder: this.$t('fleetTaskInstant.placeholder.driverName'),
            },
            {
              key: 'fleet_task.id',
              label: 'Status',
              help: true,
              tooltip: this.$t('fleetTaskInstant.tooltip.driverStatus'),
              isStatus: true,
              formatter: (val) => {
                if (val == null) {
                  return {
                    text: this.$t('fleetTaskInstant.option.available'),
                    class: 'mc-status mc-status--green',
                  }
                } else {
                  return {
                    text: this.$t('fleetTaskInstant.option.onAssignment'),
                    class: 'mc-status mc-status--yellow',
                  }
                }
              },
            },
            {
              inputName: 'phoneNo',
              key: 'phone_no',
              label: this.$t('global.field.phoneNumber'),
              help: true,
              tooltip: this.$t('fleetTaskInstant.tooltip.phoneNumber'),
              isEditable: true,
              validation: [['bail'], ['required'], ['isPhoneNumber', 'ID']],
              placeholder: this.$t('fleetTaskInstant.placeholder.whatsappNumber'),
            },
            {
              key: 'fleet_task.estimated_arrival_on',
              label: this.$t('global.field.estimatedCompletion'),
              type: 'datetime',
            },
            {
              key: 'fleet_task',
              label: this.$t('global.field.fleetTaskNumber'),
              type: 'redirectable',
              redirectTo: {
                FLEET_TASK: 'Fleet Task Detail',
                FLEET_TASK_INSTANT: 'Fleet Task Instant Detail',
              },
              redirectId: 'id',
              redirectLabel: 'number',
              formatter: (item) => {
                if (!item) {
                  return ''
                }

                if (this.hasTmsUserGroupAccess && !Helper.methods.isSuperUser()) {
                  const currentUserGroupId = store.state.fleetTaskInstant?.user_group?.id

                  if (
                    (item.user_group?.id &&
                      currentUserGroupId &&
                      item.user_group.id !== currentUserGroupId) ||
                    item.user_group === null
                  ) {
                    return '-'
                  }
                }

                return item
              },
              isClickable: (item) => {
                if (this.hasTmsUserGroupAccess && !Helper.methods.isSuperUser()) {
                  const currentUserGroupId = store.state.fleetTaskInstant?.user_group?.id

                  return (
                    item?.user_group !== null &&
                    item?.user_group?.id &&
                    currentUserGroupId &&
                    item.user_group.id === currentUserGroupId
                  )
                }

                return item !== null
              },
            },
          ],
          filterOptions: {
            default: null,
            options: [
              {
                key: 'all',
                label: this.$t('fleetTaskInstant.option.all'),
                value: null,
              },
              {
                key: 'available',
                label: this.$t('fleetTaskInstant.option.available'),
                value: true,
              },
              {
                key: 'not_available',
                label: this.$t('fleetTaskInstant.option.onAssignment'),
                value: false,
              },
            ],
          },
        },
        {
          name: 'note',
          isShow: true,
          label: this.$t('global.field.note'),
          type: 'textarea',
          placeholder: this.$t('fleetTaskInstant.placeholder.note'),
          class: 'col-12 __textarea-input',
          tooltip: this.$t('fleetTaskInstant.tooltip.note'),
        },
      ]

      return fields.filter((field) => field.isShow !== false)
    },
  },
}
